import { createContext, ReactNode } from 'react';

import { AttributeDetailsProps } from '@atom/components/common/attributeDetails/AttributeDetails';
import {
  AssetAttribute,
  AssetAttributeChangeInput,
  AssetDetail,
  AssetElement,
  AssetElementsCreateInput,
  ChangeType,
} from '@atom/types/asset';
import { SubjectType } from '@atom/types/media';

import { initialTreeState, SubItemTreeState } from './subItemTreeReducer';

export enum SubItemModalType {
  ADD_ELEMENT = 'ADD_ELEMENT',
  MOVE = 'MOVE',
  REPLACE = 'REPLACE',
  RENAME = 'RENAME',
  DELETE = 'DELETE',
  REJECT_CHANGES = 'REJECT_CHANGES',
}

export type SubItemActiveModal = {
  type: SubItemModalType;
  node: AssetElement;
};

export type SubItemPermissions = {
  canUpdate: boolean;
  canRename: boolean;
  canManageChanges: boolean;
  moveEnabled: boolean;
  replaceEnabled: boolean;
  canUpdateAttributes: boolean;
  canCreateMedia: boolean;
  canUpdateMedia: boolean;
  canDeleteMedia: boolean;
};

export type SubItemRootAsset = {
  id: string;
  name: string;
  schemaId: string;
};

// Per-row menu rules. Each surface layers its own rules on top of the
// permission flags; omitted entries fall back to the inventory behavior.
export type SubItemRowPolicy = {
  canDelete: (node: AssetElement) => boolean;
  canAddChildren: (node: AssetElement) => boolean;
  subtextPrefix?: (node: AssetElement) => string;
};

export type SubItemRootNode = {
  node: AssetElement;
  icon: ReactNode;
};

export type SubItemAttributeUpdate = {
  [id: string]: AssetAttribute;
};

// Where an element's media lives. Defaults to the inventory subject under the
// root asset.
export type SubItemMediaConfig = {
  subjectType: SubjectType;
  parentSubjectId: string;
  coverPhotoMediaId?: string;
  onCoverPhotoToggle?: (mediaId: string) => void;
};

export type SubItemsContextValue = {
  rootAsset: SubItemRootAsset;
  tree: SubItemTreeState;
  permissions: SubItemPermissions;
  rowPolicy?: SubItemRowPolicy;
  rootNode?: SubItemRootNode;
  rootSelectable?: boolean;
  media?: SubItemMediaConfig;
  getAttributeDetailsProps?: (
    detail: AssetDetail,
  ) => Partial<AttributeDetailsProps>;
  selectedId: string | null;
  setElementId: (id: string | null) => void;
  onRowClick: (node: AssetElement) => void;
  onToggleGroup: (key: string) => void;
  scrollToId: string | null;
  onScrolled: () => void;
  activeModal: SubItemActiveModal | null;
  openModal: (type: SubItemModalType, node: AssetElement) => void;
  closeModal: () => void;
  approvalErrorOpen: boolean;
  closeApprovalError: () => void;
  approveChanges: (node: AssetElement) => Promise<void>;
  rejectChanges: (node: AssetElement) => Promise<void>;
  renameElement: (id: string, name: string) => Promise<void>;
  deleteElement: (node: AssetElement) => Promise<void>;
  batchCreateElements: (input: AssetElementsCreateInput) => Promise<void>;
  updateElementAttributes: (
    id: string,
    attributes: SubItemAttributeUpdate,
  ) => Promise<void>;
  applyAttributeChange: (
    action: 'approve' | 'reject',
    input: AssetAttributeChangeInput,
  ) => Promise<void>;
};

export const defaultRowPolicy: SubItemRowPolicy = {
  canDelete: node => node.changeType !== ChangeType.DELETED,
  canAddChildren: () => true,
};

export const initialSubItemsContext: SubItemsContextValue = {
  rootAsset: { id: '', name: '', schemaId: '' },
  tree: initialTreeState,
  permissions: {
    canUpdate: false,
    canRename: false,
    canManageChanges: false,
    moveEnabled: false,
    replaceEnabled: false,
    canUpdateAttributes: false,
    canCreateMedia: false,
    canUpdateMedia: false,
    canDeleteMedia: false,
  },
  selectedId: null,
  setElementId: () => {},
  onRowClick: () => {},
  onToggleGroup: () => {},
  scrollToId: null,
  onScrolled: () => {},
  activeModal: null,
  openModal: () => {},
  closeModal: () => {},
  approvalErrorOpen: false,
  closeApprovalError: () => {},
  approveChanges: () => Promise.resolve(),
  rejectChanges: () => Promise.resolve(),
  renameElement: () => Promise.resolve(),
  deleteElement: () => Promise.resolve(),
  batchCreateElements: () => Promise.resolve(),
  updateElementAttributes: () => Promise.resolve(),
  applyAttributeChange: () => Promise.resolve(),
};

const SubItemsContext = createContext<SubItemsContextValue>(
  initialSubItemsContext,
);

export default SubItemsContext;
